import React, { useEffect, useState } from "react"
import Breadcrumbs from "components/Common/Breadcrumb"
import { Link, useLocation, useNavigate } from "react-router-dom"
import { useDispatch } from "react-redux"
import {
  Card,
  CardBody,
  CardTitle,
  Col,
  Row,
  Table,
  UncontrolledTooltip,
} from "reactstrap"
import BackButton from "components/Shared/BackButton"
import LtrText from "components/Shared/LtrText"
import classNames from "classnames"
import { getApiLogDetails } from "store/actions/apilogsAction"
import { useSelector } from "react-redux"
import formatDateTime from "components/Shared/formatDateTime"
import {  showAlert } from "store/reducers/alertReducer"
import { ShimmerThumbnail } from "react-shimmer-effects"
import isPermissionEnabled from "common/isPermissionEnabled"
import { useTranslation } from "react-i18next"

const ApiLogsDetails = () => {
  const { t } = useTranslation()

  document.title = t("API Logs Details ")
  const location = useLocation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const searchParams = new URLSearchParams(location.search)
  const id = searchParams.get("id")

  const { loading, apiLogDetails } = useSelector(state => state.APILogs)

  const { userRole, accountType } = useSelector(state => state.Login)

  useEffect(() => {
    const isPermissionList = isPermissionEnabled(
      userRole,
      13,
      "api_logs_1",
      accountType
    )

    if (!isPermissionList) {
      navigate("/dashboard")
    }
  }, [userRole])

  useEffect(() => {
    fetchApiLogDetails()
  }, [])

  // useEffect(() => {
  //   console.log(apiLogDetails)
  // }, [apiLogDetails])

  const fetchApiLogDetails = () => {
    dispatch(getApiLogDetails(id))
  }

  const [activeTab, setActiveTab] = useState("PAYLOAD")

  const onTabsChange = type => {
    setActiveTab(type)
  }

  const formattedData = data => data?.replace(/\n/g, "<br>")

  const copyToClipboard = data => {
    const dataString = JSON.stringify(data, null, 2)

    const textarea = document.createElement("textarea")
    textarea.value = dataString
    textarea.setAttribute("readonly", "")
    textarea.style.position = "absolute"
    textarea.style.left = "-9999px"
    document.body.appendChild(textarea)

    try {
      textarea.select()
      const successful = document.execCommand("copy")
      if (successful) {
        dispatch(
          showAlert({ text: t("Data copied to clipboard!"), type: "success" })
        )
      } else {
        dispatch(showAlert({ text: t("Failed to copy"), type: "failed" }))
      }
    } catch (err) {
      dispatch(showAlert({ text: t("Failed to copy"), type: "failed" }))
    } finally {
      document.body.removeChild(textarea)
    }
  }

  const downloadJSON = data => {
    const dataString = JSON.stringify(data, null, 2)
    const blob = new Blob([dataString], { type: "application/json" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = "data.json"
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs />
        <Row>
          <Col lg={5}>
            <Card>
              <CardBody>
                <CardTitle className="d-flex align-items-center">
                  <BackButton /> {t("API Logs Details")}
                </CardTitle>
                <div className="table-responsive">
                  {loading ? (
                    <ShimmerThumbnail height={550} rounded />
                  ) : Object.values(apiLogDetails).length === 0 ? (
                    <p className="text-center my-2">{t("No API log found")}</p>
                  ) : (
                    <Table className="table-nowrap mb-0">
                      <tbody>
                        <tr>
                          <th scope="row">{t("Id")}</th>
                          <td>{apiLogDetails?._id}</td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Customer Name")}</th>
                          <td className="text-capitalize">
                            {apiLogDetails?.userInfo?.name || "-"}
                          </td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Customer Email")}</th>
                          <td className="text-capitalize">
                            {apiLogDetails?.userInfo?.email || "-"}
                          </td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Customer Phone Number")}</th>
                          <td>
                            {apiLogDetails?.userInfo?.phone ? (
                              <LtrText>{apiLogDetails.userInfo.phone}</LtrText>
                            ) : (
                              "-"
                            )}
                          </td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Date & Time")}</th>
                          <td>
                            {formatDateTime(apiLogDetails?.createdAt) || "-"}
                          </td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Method")}</th>
                          <td>
                            <p className="bg-primary w_fit_content px-2 text-white rounded fw-bold">
                              <small>
                                {apiLogDetails?.data?.method || "-"}
                              </small>
                            </p>
                          </td>
                        </tr>
                        <tr>
                          <th scope="row">{t("IP Address")}</th>
                          <td>{apiLogDetails?.data?.ipAddress}</td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Path")}</th>
                          <td>{apiLogDetails?.data?.url}</td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Status Code")}</th>
                          <td>
                            <span
                              className={`${
                                apiLogDetails?.data?.responseStatus === 200
                                  ? "bg-success"
                                  : apiLogDetails?.data?.responseStatus === 500
                                  ? "bg-danger"
                                  : "bg-warning"
                              }
                                 text-white fw-bold px-1 rounded`}
                            >
                              <small>
                                {apiLogDetails?.data?.responseStatus}
                              </small>
                            </span>
                          </td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Duration")}</th>
                          <td>{apiLogDetails?.data?.duration}</td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Memory Usage")}</th>
                          <td>{apiLogDetails?.data?.memoryUsed}</td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Status")}</th>
                          <td>
                            {" "}
                            <span
                              className={`
                              px-2 rounded text-white fw-bold
                              ${
                                apiLogDetails?.data?.status === 1
                                  ? "bg-success"
                                  : "bg-danger"
                              }`}
                            >
                              <small>
                                {apiLogDetails?.data?.status === 1
                                  ? t("Success")
                                  : t("Failed")}
                              </small>
                            </span>
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                  )}
                </div>
              </CardBody>
            </Card>
          </Col>
          <Col lg={7}>
            <Card
              className="full_height_card"
              style={{
                maxHeight: "calc(100% - 24)",
                overflow: "auto",
                maxHeight: 610,
              }}
            >
              <CardBody>
                <ul
                  className="nav nav-pills bg-white "
                  // style={{
                  //   minWidth: 80,
                  //   position: "fixed",
                  //   width: "90%",
                  //   overflowX: hidden,
                  // }}
                >
                  <li className="nav-item">
                    <Link
                      to="#"
                      className={classNames(
                        { active: activeTab === "PAYLOAD" },
                        "nav-link"
                      )}
                      onClick={(e) => {
                        e.preventDefault()
                        onTabsChange("PAYLOAD")
                      }}
                    >
                      {t("Payload")}
                    </Link>
                  </li>
                  <li className="nav-item">
                    <Link
                      to="#"
                      className={classNames(
                        { active: activeTab === "HEADERS" },
                        "nav-link"
                      )}
                      onClick={(e) => {
                        e.preventDefault()
                        onTabsChange("HEADERS")
                      }}
                    >
                      {t("Headers")}
                    </Link>
                  </li>
                  <li className="nav-item">
                    <Link
                      to="#"
                      className={classNames(
                        { active: activeTab === "RESPONSE" },
                        "nav-link"
                      )}
                      onClick={(e) => {
                        e.preventDefault()
                        onTabsChange("RESPONSE")
                      }}
                    >
                      {t("Response")}
                    </Link>
                  </li>
                </ul>
                {loading ? (
                  <div className="mt-3">
                    <ShimmerThumbnail height={520} rounded />
                  </div>
                ) : (
                  <div
                    className="api_res_inner bg-black p-3 text-white rounded mt-4 position-relative"
                    style={{ height: "calc(100% - 60px)" }}
                  >
                    {activeTab === "PAYLOAD" && (
                      <>
                        <pre
                          dangerouslySetInnerHTML={{
                            __html: formattedData(
                              JSON.stringify(
                                apiLogDetails?.data?.requests,
                                null,
                                2
                              )
                            ),
                          }}
                        />
                        <i
                          className="mdi mdi-content-copy cursor-pointer"
                          id="copytooltip"
                          style={{
                            position: "absolute",
                            top: 10,
                            right: 50,
                            fontSize: 18,
                          }}
                          onClick={() =>
                            copyToClipboard(apiLogDetails?.data?.requests)
                          }
                        ></i>
                        <i
                          style={{
                            position: "absolute",
                            top: 10,
                            right: 20,
                            fontSize: 18,
                          }}
                          id="jsontooltip"
                          className="mdi mdi-download cursor-pointer"
                          onClick={() =>
                            downloadJSON(apiLogDetails?.data?.requests)
                          }
                        ></i>
                        <UncontrolledTooltip
                          placement="top"
                          target="copytooltip"
                        >
                          {t("Copy To Clipboard")}
                        </UncontrolledTooltip>
                        <UncontrolledTooltip
                          placement="top"
                          target="jsontooltip"
                        >
                          {t("Download JSON")}
                        </UncontrolledTooltip>
                      </>
                    )}
                    {activeTab === "HEADERS" && (
                      <>
                        <pre
                          dangerouslySetInnerHTML={{
                            __html: formattedData(
                              JSON.stringify(
                                apiLogDetails?.data?.headers,
                                null,
                                2
                              )
                            ),
                          }}
                        />
                        <i
                          className="mdi mdi-content-copy cursor-pointer"
                          id="copytooltip"
                          style={{
                            position: "absolute",
                            top: 10,
                            right: 50,
                            fontSize: 18,
                          }}
                          onClick={() =>
                            copyToClipboard(apiLogDetails?.data?.headers)
                          }
                        ></i>
                        <i
                          style={{
                            position: "absolute",
                            top: 10,
                            right: 20,
                            fontSize: 18,
                          }}
                          id="jsontooltip"
                          className="mdi mdi-download cursor-pointer"
                          onClick={() =>
                            downloadJSON(apiLogDetails?.data?.headers)
                          }
                        ></i>
                        <UncontrolledTooltip
                          placement="top"
                          target="copytooltip"
                        >
                          {t("Copy To Clipboard")}
                        </UncontrolledTooltip>
                        <UncontrolledTooltip
                          placement="top"
                          target="jsontooltip"
                        >
                          {t("Download JSON")}
                        </UncontrolledTooltip>
                      </>
                    )}
                    {activeTab === "RESPONSE" && (
                      <>
                        <pre
                          dangerouslySetInnerHTML={{
                            __html: formattedData(
                              JSON.stringify(
                                apiLogDetails?.data?.response,
                                null,
                                2
                              )
                            ),
                          }}
                        />
                        <i
                          className="mdi mdi-content-copy cursor-pointer"
                          id="copytooltip"
                          style={{
                            position: "absolute",
                            top: 10,
                            right: 50,
                            fontSize: 18,
                          }}
                          onClick={() =>
                            copyToClipboard(apiLogDetails?.data?.response)
                          }
                        ></i>
                        <i
                          style={{
                            position: "absolute",
                            top: 10,
                            right: 20,
                            fontSize: 18,
                          }}
                          id="jsontooltip"
                          className="mdi mdi-download cursor-pointer"
                          onClick={() =>
                            downloadJSON(apiLogDetails?.data?.response)
                          }
                        ></i>
                        <UncontrolledTooltip
                          placement="top"
                          target="copytooltip"
                        >
                          {t("Copy To Clipboard")}
                        </UncontrolledTooltip>
                        <UncontrolledTooltip
                          placement="top"
                          target="jsontooltip"
                        >
                          {t("Download JSON")}
                        </UncontrolledTooltip>
                      </>
                    )}
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  )
}

export default ApiLogsDetails
